import React, { useState, useEffect, useMemo, useCallback } from 'react';
import ApprovalBonusListPresentation from '../presentation/ApprovalBonusList.jsx';
import { message } from 'antd';
import { HomeServiceProvider } from 'src/home/service/home.services.jsx';
import { useSelector } from 'react-redux'
import { gql } from 'graphql-tag';
import { withApollo } from 'react-apollo';

const QUERY_BADGE_ASSIGNMENTS = gql`
  query QueryBadgeAssignments($companyId: ID!, $nextToken: String) {
    queryBadgeAssignments(
      companyId: $companyId
      nextToken: $nextToken
    ) {
      items {
        approvalStatus
        awardedBy
        id
        badgeId
        badge {
            id
            name
            snappyGrantId
            snappyGrant {
                id
                name
                costInCurrency
                externalSnappyId
                snappyCampaignTierId
            }
        }
        rewardBonus
        companyId
        userGroupId
        userId
        createdAt
        updatedAt
        user {
          id
          firstName
          lastName
          emailAddress
          managerEmployeeId
          recognitionEvents {
            currentBudget
          }
        }
        declineReason
      }
      nextToken
    }
  }
`;



const ApprovalBonusListContainer = ({ client }) => {
    const [approvalBonus, setapprovalBonus] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [approvalCount, setApprovalCount] = useState()
    const [filterView, setFilterView] = useState('mySent'); // 'mySent', 'all', 'myTeam'
    const currentUser = useSelector((state) => state.user?.currentUser) || {};
    const companyId = currentUser?.company?.id;

    useEffect(() => {
        if (client && companyId) {
            getApprovalBonusList();
        }
    }, [client, companyId, filterView]);

    const getApprovalBonusList = async () => {
        try {
            setIsLoading(true);
            let unfilteredItems = [];
            
            const queryAssignmentsRecursively = async (nextToken = null) => {
                const { data, error } = await client.query({
                    query: QUERY_BADGE_ASSIGNMENTS,
                    variables: {
                        companyId: companyId,
                        nextToken: nextToken
                    },
                    fetchPolicy: 'network-only'
                });
                
                if (error) {
                    throw error;
                }
                if (data?.queryBadgeAssignments?.items) {
                    unfilteredItems.push(...data.queryBadgeAssignments.items);
                }
                if (data.queryBadgeAssignments.nextToken) {
                    await queryAssignmentsRecursively(data.queryBadgeAssignments.nextToken);
                }
            }

            await queryAssignmentsRecursively();
            
            // Apply filter based on current view
            let items;
            switch (filterView) {
                case 'all':
                    items = unfilteredItems;
                    break;
                case 'myTeam':
                    items = unfilteredItems.filter(item => 
                        item.user?.managerEmployeeId === currentUser?.employeeId
                    );
                    break;
                case 'mySent':
                default:
                    items = unfilteredItems.filter(item => item.awardedBy === currentUser?.id);
                    break;
            }
            
            const transformedData = items.map(item => ({
                id: item.id,
                recipient: item.user ? `${item.user.firstName} ${item.user.lastName}` : item.userId,
                bonus: item?.badge?.snappyGrant?.costInCurrency ?? item.rewardBonus ?? 0,
                reward: item.badge ? item.badge.name : item.badgeId,
                created: item.createdAt ? new Date(item.createdAt) : new Date(),
                completed: item.approvalStatus !== 'Pending' && item.updatedAt ? new Date(item.updatedAt) : null,
                status: item.approvalStatus || 'Pending',
                userData: item.user,
                badgeData: item.badge,
                declineReason: item.declineReason
            }));

            const approvedCount = items.filter(item => item.approvalStatus === 'Approved').length;
            const pendingCount = items.filter(item => item.approvalStatus === 'Pending').length;
            const declinedCount = items.filter(item => item.approvalStatus === 'Declined').length;
            
            const totalBonus = items.reduce((sum, item) => sum + (item.badge?.snappyGrant?.costInCurrency || 0), 0);
            const approvedBonus = items
                .filter(item => item.approvalStatus === 'Approved')
                .reduce((sum, item) => sum + (item.badge?.snappyGrant?.costInCurrency || 0), 0);
            const pendingBonus = items
                .filter(item => item.approvalStatus === 'Pending' || !item.approvalStatus)
                .reduce((sum, item) => sum + (item.badge?.snappyGrant?.costInCurrency || 0), 0);
            
            const countData = {
                approvalStatusCounts: [
                    { name: 'Approved', totals: approvedCount, color: 'oklch(0.7903 0.136 192.97)' }, // P3 colorspace of #14D6D3
                    { name: 'Pending', totals: pendingCount, color: 'oklch(0.6757 0.187 349.98)' }, // P3 colorspace of #E75DA7
                    { name: 'Declined', totals: declinedCount, color: 'oklch(0.58 0.26 295)' } // P3 colorspace of #7942E7
                ],
                rewardPointsValues: [
                    { name: 'Total Rewards', totals: totalBonus, color: 'oklch(0.5307 0.1791 144.1)' }, // P3 colorspace of #1D8627
                    { name: 'Approved', totals: approvedBonus, color: 'oklch(0.7099 0.1414 141.23)' },
                    { name: 'Pending', totals: pendingBonus, color: 'oklch(0.8462 0.1426 141.19)' }
                ]
            };
            
            setApprovalCount(countData);
            setapprovalBonus(transformedData);
        } catch (error) {
            console.error('Error fetching badge assignments:', error);
            message.error('Failed to fetch recognition details! Try again later.');
        } finally {
            setIsLoading(false);
        }
    };

    // Memoize header text to prevent unnecessary rerenders
    const headerText = useMemo(() => {
        switch (filterView) {
            case 'all':
                return 'All Recognitions';
            case 'myTeam':
                return "My Team's Recognitions";
            case 'mySent':
            default:
                return 'My Sent Recognitions';
        }
    }, [filterView]);

    // Memoize the setFilterView callback to prevent recreating on every render
    const handleFilterChange = useCallback((newFilter) => {
        setFilterView(newFilter);
    }, []);

    return (
        <>
            <HomeServiceProvider>
                <ApprovalBonusListPresentation
                    approvalBonus={approvalBonus}
                    isLoading={isLoading}
                    approvalCount={approvalCount}
                    client={client}
                    filterView={filterView}
                    setFilterView={handleFilterChange}
                    headerText={headerText}
                />
            </HomeServiceProvider>
        </>
    );
};

export default withApollo(ApprovalBonusListContainer);
